import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { fetchUnitInbox, markUnitNotificationsRead, type UnitInbox } from "./unitNotifications";
import { unitItems } from "./unitBell";

const key = (cid: string | null) => ["unitNotifications", "inbox", cid] as const;

/**
 * Bandeja de la campana del mapa. Se refresca al instante con el evento
 * `notification` del socket (dumax-socket lo emite a la sala user:<uid>; antes
 * nadie lo escuchaba) y cada minuto, porque geocercas y viajes no pasan por
 * ese canal.
 */
export function useUnitInbox(enabled: boolean) {
  const cid = useCompaniesStore((s) => s.companyId);
  const qc = useQueryClient();

  // El listener vive en el store del socket (connect() hace removeAllListeners()).
  const tick = useVehiclesSocketStore((s) => s.unitNotificationTick);
  useEffect(() => {
    if (enabled && tick > 0) qc.invalidateQueries({ queryKey: key(cid) });
  }, [enabled, tick, cid, qc]);

  return useQuery({
    queryKey: key(cid),
    queryFn: () => fetchUnitInbox(cid!),
    enabled: enabled && !!cid,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * La bandeja de UNA unidad (campanita de su fila y de su tarjeta). Comparte la
 * consulta de la campana global (misma llave: una sola petición para toda la
 * lista) y no escucha el socket ni fija intervalo: eso lo hace la campana del
 * header, que está en todas las apps. Con `select`, cada fila sólo se vuelve a
 * pintar si cambian SUS notificaciones.
 */
export function useUnitInboxFor(deviceId: string, vehicleId?: string | null) {
  const cid = useCompaniesStore((s) => s.companyId);
  const select = useCallback(
    (d: UnitInbox) => {
      const items = unitItems(d.items, deviceId, vehicleId);
      return { items, unread: items.filter((n) => !n.read).length };
    },
    [deviceId, vehicleId],
  );
  return useQuery({ queryKey: key(cid), queryFn: () => fetchUnitInbox(cid!), enabled: !!cid, staleTime: 30_000, select });
}

/** Marca leídas (unas o todas) con actualización optimista del contador. */
export function useMarkUnitRead() {
  const cid = useCompaniesStore((s) => s.companyId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[] } | { all: true }) => markUnitNotificationsRead(cid!, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: key(cid) });
      const prev = qc.getQueryData<UnitInbox>(key(cid));
      if (prev) {
        const hit = (id: string) => "all" in body || body.ids.includes(id);
        const items = prev.items.map((n) => (hit(n.id) ? { ...n, read: true } : n));
        qc.setQueryData<UnitInbox>(key(cid), { items, unread: items.filter((n) => !n.read).length });
      }
      return { prev };
    },
    onError: (_e, _b, ctx) => ctx?.prev && qc.setQueryData(key(cid), ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: key(cid) }),
  });
}
